import { createWithEqualityFn } from 'zustand/traditional';
import { IElementID } from '@shared/models/project.model';
import { devtools } from 'zustand/middleware';
import { IUIState } from './ui.model';
import { mergeObservedElementIds, stringToPage } from './ui.helper';
import { omit } from '@shared/helpers/object.helpers';
import { resetUndoRedoHistory } from '@shared/helpers/undo-redo.helpers';
import { shallow } from 'zustand/shallow';
import { getHmrStoreState, initHmrStore } from '../../helpers/vite.helpers';

const RECENTLY_CHANGED_TOGGLE_TIMEOUT = 15000;
const STORE_NAME = 'ui';

let recentlyChangedTimeoutRef: number | undefined;

type OptionalUIStateKeys = {
  [K in keyof IUIState]-?: undefined extends IUIState[K] ? K : never;
}[keyof IUIState];

// Declare every optional key up front so the initial state has the same shape as all
// later states. shallow({ a: 1 }, { a: 1, b: undefined }) is false, so a key appearing
// first on update would make subscribers render an extra time.
const initialOptionalState: Record<OptionalUIStateKeys, undefined> = {
  addedElementId: undefined,
  addedPropertyId: undefined,
  selectedElementId: undefined,
  selectedVersionId: undefined,
  recentlyUndoRedoElementId: undefined,
  renamingId: undefined,
  selectedProjectFolderId: undefined,
};

export const useUIStore = createWithEqualityFn<IUIState>()(
  devtools(
    (set, get) => {
      return {
        selectedPage: stringToPage(''),
        observedElementIds: {},
        showRenderDebug: false,
        reportCoverImageRequest: 'none',
        designImportRequest: 'none',
        ...initialOptionalState,

        ...getHmrStoreState(STORE_NAME),

        setAddedElementId: (addedElementId?: string) => {
          if (addedElementId !== get().addedElementId) {
            set({ addedElementId });
          }
        },
        setAddedPropertyId: (addedPropertyId?: string) => {
          if (addedPropertyId !== get().addedPropertyId) {
            set({ addedPropertyId });
          }
        },

        setSelectedElementId: (id) => {
          const { selectedElementId } = get();
          if (id !== selectedElementId) set({ selectedElementId: id });
        },

        setSelectedVersionId: (id) => {
          const { selectedVersionId } = get();
          if (id !== selectedVersionId) set({ selectedVersionId: id });
        },

        setSelectedPage: (page) => {
          page = stringToPage(page);
          if (page !== get().selectedPage) {
            set({ selectedPage: page });
            resetUndoRedoHistory();
          }
        },

        setRecentlyUndoRedoElementId: (id: IElementID | undefined) => {
          if (id === get().recentlyUndoRedoElementId) {
            return;
          }
          if (recentlyChangedTimeoutRef) {
            window.clearTimeout(recentlyChangedTimeoutRef);
          }
          set({ recentlyUndoRedoElementId: id });

          if (id) {
            recentlyChangedTimeoutRef = window.setTimeout(() => {
              set({ recentlyUndoRedoElementId: undefined });
            }, RECENTLY_CHANGED_TOGGLE_TIMEOUT);
          }
        },

        clearSelections: (...exclude) =>
          set(
            omit(
              { addedElementId: undefined, addedPropertyId: undefined },
              ...exclude,
            ),
          ),

        clearSelection: (target) => set({ [target]: undefined }),

        setRenamingId: (renamingId) => set({ renamingId }),

        setSelectedProjectFolderId: (selectedProjectFolderId) =>
          set({ selectedProjectFolderId }),

        addObservedElementIds: (observedElementIds) => {
          const merged = mergeObservedElementIds(
            get().observedElementIds,
            observedElementIds,
          );
          if (!merged) return;
          set({ observedElementIds: merged });
        },

        setShowRenderDebug: (show: boolean) => set({ showRenderDebug: show }),

        setReportCoverImageRequest: (reportCoverImageRequest) =>
          set({ reportCoverImageRequest }),

        setDesignImportRequest: (designImportRequest) =>
          set({ designImportRequest }),
      };
    },
    { name: STORE_NAME },
  ),
  shallow,
);

// In HMR mode we need to keep the store state between reloads
initHmrStore(STORE_NAME, useUIStore);

export default useUIStore;
